import { alpha, useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import LabelLegacy from 'src/components/label/labelLegacy';

type StatusTone = 'success' | 'info' | 'warning' | 'error' | 'default' | 'primary';

type StatusConfig = {
  label: string;
  icon: string;
  tone: StatusTone;
};

type AchievementStatusBadgeProps = {
  status?: string | null;
  label?: string | null;
  variant?: 'outlined' | 'soft';
};

const STATUS_CONFIG: Record<string, StatusConfig> = {
  active: { label: 'Ativo', icon: 'mdi:check-circle-outline', tone: 'success' },
  inactive: { label: 'Inativo', icon: 'mdi:close-circle-outline', tone: 'error' },
  paused: { label: 'Pausado', icon: 'mdi:pause-circle-outline', tone: 'warning' },
  finished: { label: 'Finalizado', icon: 'mdi:flag-checkered', tone: 'default' },
  ongoing: { label: 'Em andamento', icon: 'mdi:play-circle-outline', tone: 'success' },
  upcoming: { label: 'Em breve', icon: 'mdi:calendar-clock-outline', tone: 'info' },
  scheduled: { label: 'Agendada', icon: 'mdi:calendar-clock-outline', tone: 'info' },
};

export function getAchievementStatusOption(status?: string | null, label?: string | null) {
  const normalizedStatus = String(status || '').toLowerCase();
  const option = STATUS_CONFIG[normalizedStatus];

  if (option) {
    return {
      ...option,
      label: label || option.label,
    };
  }

  return {
    label: label || status || '-',
    icon: 'mdi:help-circle-outline',
    tone: 'default' as const,
  };
}

export default function AchievementStatusBadge({
  status,
  label,
  variant = 'outlined',
}: AchievementStatusBadgeProps) {
  const theme = useTheme();
  const option = getAchievementStatusOption(status, label);
  const paletteColor =
    option.tone === 'default' ? theme.palette.grey[600] : theme.palette[option.tone].main;

  if (variant === 'soft') {
    const isFinished = String(status || '').toLowerCase() === 'finished';

    return (
      <LabelLegacy
        color={option.tone}
        sx={isFinished ? { color: 'common.white', bgcolor: 'grey.900' } : undefined}
      >
        {option.label}
      </LabelLegacy>
    );
  }

  return (
    <Label
      variant="outlined"
      color="default"
      sx={{
        gap: 0.75,
        px: 1.15,
        height: 28,
        borderRadius: 1,
        borderStyle: 'dashed',
        borderColor: alpha(paletteColor, 0.55),
        backgroundColor: alpha(paletteColor, 0.08),
        color: paletteColor,
        fontWeight: 800,
        letterSpacing: 0,
        textTransform: 'none',
      }}
    >
      <Iconify icon={option.icon} sx={{ width: 16, height: 16 }} />
      {option.label}
    </Label>
  );
}
